<template>
  <div class="login-wrap flex-column">
    <div class="header flex-column flex-middle">
      <van-image width="44" height="44" fit="contain" :src="require(`@/assets/login-icon.png`)" />
      <p>票天下接单系统</p>
    </div>
    <div v-if="!isDev" class="main-card">
      <van-cell-group v-if="!isApplied">
        <div v-if="isResubmitting" class="resubmit-tip">
          <strong>上次审核未通过</strong>
          <p>{{ rejectReason || '请根据实际情况修改资料后重新提交。' }}</p>
        </div>
        <van-field v-model="form.phone" type="number" clearable ref="phone" maxlength="11" placeholder="请输入您的手机号" />
        <!-- <van-field v-model="form.verifyCode"  clearable ref="verifyCode" maxlength="24" placeholder="输入手机版动态密码">
          <template #button>
            <van-button size="small" type="primary">发送验证码</van-button>
            <div class="verifyCodeBtn" @click="handleVerifyCode">{{verifyText}}</div>
          </template>
        </van-field> -->
        <div class="phone-tip">* 手机号为接单唯一凭证，请确保手机号正确</div>
        <p class="phone-tip">至少填写微信、支付宝中的一种收款方式，也可以都填。</p>
        <p class="draft-tip">未点击“确认资料并提交审核”前，系统不会保存本次填写内容。</p>
        <receipt-form ref="wechatReceipt" kind="wechat" :saved="signupReceipts.find(r => r.kind === 'wechat') || null" :editable="isResubmitting" />
        <receipt-form ref="alipayReceipt" kind="alipay" :saved="signupReceipts.find(r => r.kind === 'alipay') || null" :editable="isResubmitting" />
        <p v-if="signupLoadError" class="signup-load-error">{{ signupLoadError }}</p>
        <van-button block type="primary" :loading="signupSubmitting || signupLoading" :disabled="signupSubmitting || signupLoading" @click="handleSignupAction">{{ signupButtonText }}</van-button>
      </van-cell-group>
      
      <!-- 申请状态显示 -->
      <div v-if="isApplied" class="apply-status">
        <div class="status-icon">
          <van-icon :name="applyStatus === 'rejected' ? 'close' : 'clock-o'" size="48" :color="applyStatus === 'rejected' ? '#ee0a24' : '#1989fa'" />
        </div>
        <div class="status-title">{{ applyTitle }}</div>
        <div class="status-message">{{ applyMessage }}</div>
        <div v-if="applyStatus === 'rejected' && rejectReason" class="reject-reason">
          <strong>拒绝原因</strong>
          <p>{{ rejectReason }}</p>
        </div>
        <div v-if="form.phone || signupReceipts.length" class="submitted-summary">
          <van-cell v-if="form.phone" title="手机号" :value="form.phone" />
          <div v-for="receipt in signupReceipts" :key="receipt.kind" class="submitted-receipt">
            <div>
              <strong>{{ receipt.kind === 'wechat' ? '微信收款' : '支付宝收款' }}</strong>
              <span>{{ receipt.name }} · {{ receipt.account || '已上传收款码' }}</span>
            </div>
            <van-image v-if="receipt.qr_code" width="64" height="64" fit="contain" :src="receipt.qr_code" @click="previewReceipt(receipt.qr_code)" />
          </div>
        </div>
        <van-button v-if="applyStatus === 'rejected' && canResubmit" block type="primary" @click="editRejectedApplication">修改资料并重新申请</van-button>
        <van-button block type="primary" @click="refreshPage">刷新审核结果</van-button>
      </div>
    </div>
    <!-- 仅本地开发环境显示，生产环境仍使用微信授权和手机号绑定 -->
    <div v-if="isDev" class="dev-login main-card">
      <div class="dev-title">开发环境登录</div>
      <van-cell-group>
        <van-field v-model.trim="devUsername" clearable autocomplete="username" placeholder="账号" />
        <van-field
          v-model="devPassword"
          type="password"
          clearable
          autocomplete="current-password"
          placeholder="密码"
          @keyup.enter.native="devLogin"
        />
      </van-cell-group>
      <div class="dev-tip">仅连接本地 debug 后端时可用</div>
      <van-button
        class="dev-login-button"
        type="primary"
        color="#8565bd"
        block
        :loading="devLoginLoading"
        loading-text="登录中..."
        @click="devLogin"
      >
        登录
      </van-button>
    </div>

    <div class="footer flex-row fx-end flex-center">
      <span>
        若存在登录问题，请联系相应管理员
      </span>
    </div>
    
    <!-- PC端微信刷新提示 -->
    <div v-if="showPCWeChatTip" class="pc-wechat-tip">
      <van-icon name="info-o" size="16" />
      <span>PC端微信需要刷新页面获取授权，请刷新重试</span>
      <van-button size="small" type="primary" @click="refreshPage">刷新页面</van-button>
    </div>
    <div class="version-info">
      v{{ version }}
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapActions, mapGetters } from 'vuex';
import { validField } from '@/libs/common-utils/common-utils';
import { ImagePreview } from 'vant';
import router from '@/router';
import store from '@/store';
import { wxLogin, showDebugInfo } from '@/libs/wx-utils';
import config from '@/config';
import ReceiptForm from '@/components/ReceiptForm.vue';
import { receiptRequest } from '@/api-service/receipt';

export default {
  components: { ReceiptForm },
  data() {
    return {
      form: {
        phone: '',
        verifyCode: '',
      },
      verifyText: '获取验证码',
      disabledVerify: false,
      loadingVerify: false,
      redirectUri: '',
      version: config.version,
      applyStatus: '', // 申请状态
      applyMessage: '', // 申请返回消息
      rejectReason: '',
      canResubmit: false,
      isResubmitting: false,
      signupReceipts: [],
      signupLoading: false,
      signupLoaded: false,
      signupLoadError: '',
      signupSubmitting: false,
      isApplied: false, // 是否已申请
      showPCWeChatTip: false, // 是否显示PC端微信提示
      devUsername: '', // 开发环境账号
      devPassword: '', // 开发环境密码
      devLoginLoading: false,
    };
  },
  methods: {
    ...mapActions(['sendVerifyCode', 'loginByVerify', 'loginByPassword']),
    handleVerifyCode() {
      if(this.loadingVerify){
        return;
      }
      if (this.form.phone == '') {
        this.$toast('请输入手机号码');
        return false;
      }
      let valid = validField(this.form.phone, 'phoneNumber');
      if (!valid) {
        this.$toast('请输入正确的手机号码');
        return;
      }
      this.loadingVerify = true;
      this.sendVerifyCode(this.form.phone).then(() => {
        this.$toast.success('发送成功');
        this.$refs.verifyCode.focus();
        this.loadingVerify = false;
      });
    },
    closeTimer() {
      clearInterval(this.timer);
      this.timer = null;
    },
    async loadSignup() {
      const token = sessionStorage.getItem('bid_signup_token');
      if (!token) {
        this.signupLoaded = false;
        this.signupLoadError = '微信注册凭证未加载，请点击下方按钮重新获取授权';
        return false;
      }
      this.signupLoading = true;
      try {
        const info = await receiptRequest('/public/user/signup/info', { signup_token: token });
        this.form.phone = info.phone || ''; this.signupReceipts = info.receipts || []; this.signupLoaded = true; this.signupLoadError = ''; this.rejectReason = info.reject_reason || ''; this.canResubmit = Boolean(info.can_resubmit); this.isResubmitting = false;
        if (info.status === 2) { this.isApplied = true; this.applyStatus = 'rejected'; this.applyMessage = this.canResubmit ? '入驻审核未通过，请修改资料后重新提交审核' : '账号已停用，请联系管理员'; }
        else if (info.status === 1) { window.location.replace('/'); }
        else if (info.submitted) { this.isApplied = true; this.applyStatus = 'pending'; this.applyMessage = '资料已提交，请等待审核，审核结果将通过公众号通知'; this.rejectReason = ''; }
        else { this.isApplied = false; this.applyStatus = 'draft'; this.rejectReason = ''; }
        return true;
      } catch (e) {
        this.signupLoaded = false;
        this.signupLoadError = e.message || '注册资料加载失败，请重试';
        if (this.signupLoadError.includes('过期') || this.signupLoadError.includes('凭证失效')) {
          sessionStorage.removeItem('bid_signup_token');
          this.signupLoadError += '，请重新获取微信授权';
        }
        this.$toast.fail(this.signupLoadError);
        return false;
      } finally { this.signupLoading = false; }
    },
    async handleSignupAction() {
      if (this.signupLoaded) {
        await this.applyBinding();
        return;
      }
      if (!sessionStorage.getItem('bid_signup_token')) {
        this.refreshPage();
        return;
      }
      await this.loadSignup();
    },
    async applyBinding() {
      if (this.signupSubmitting) return;
      try {
        if (!validField(this.form.phone, 'phoneNumber')) throw new Error('请输入正确的手机号');
        const receipts = [this.$refs.wechatReceipt.value(), this.$refs.alipayReceipt.value()].filter(Boolean);
        if (!receipts.length && !this.signupReceipts.length) throw new Error('请至少填写一种收款方式');
        await this.$dialog.confirm({ title: '确认入驻资料', message: '请核对手机号、收款姓名和收款凭据。提交后收款资料仅管理员可修改。' });
        this.signupSubmitting = true;
        const res = await receiptRequest('/public/user/code/apply', { phone: this.form.phone, signup_token: sessionStorage.getItem('bid_signup_token'), receipts });
        this.signupReceipts = [...this.signupReceipts, ...receipts].reduce((list, receipt) => {
          const index = list.findIndex(item => item.kind === receipt.kind);
          if (index >= 0) list.splice(index, 1, receipt); else list.push(receipt);
          return list;
        }, []);
        this.isApplied = true; this.applyMessage = res.msg; this.applyStatus = 'pending'; this.rejectReason = ''; this.canResubmit = false; this.isResubmitting = false;
      } catch (e) {
        if (e && e.message) {
          if (e.message.includes('过期')) {
            sessionStorage.removeItem('bid_signup_token');
            this.signupLoaded = false;
            this.signupLoadError = '微信注册凭证已过期，请重新获取授权后提交';
          }
          this.$toast.fail(e.message);
        }
      } finally { this.signupSubmitting = false; }
    },
    editRejectedApplication() {
      this.isApplied = false;
      this.isResubmitting = true;
    },
    previewReceipt(src) {
      ImagePreview([src]);
    },

    // 复制手机号
    async copyPhoneNumber() {
      const phoneNumber = '18121083385';
      try {
        if (navigator && navigator.clipboard) {
          await navigator.clipboard.writeText(phoneNumber);
          this.$toast.success('手机号已复制到剪贴板');
        } else {
          // 兼容性处理
          const input = document.createElement('input');
          input.value = phoneNumber;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
          this.$toast.success('手机号已复制到剪贴板');
        }
      } catch (err) {
        console.error('复制失败:', err);
        this.$toast.fail('复制失败，请手动复制');
      }
    },
    
    // 刷新页面
    refreshPage() {
      this.$toast('正在刷新页面...');
      setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('code'); url.searchParams.delete('state');
        window.location.replace(url.toString());
      }, 1000);
    },

    async devLogin() {
      if (!this.devUsername || !this.devPassword) {
        this.$toast('请输入账号和密码');
        return;
      }
      if (this.devLoginLoading) {
        return;
      }
      this.devLoginLoading = true;
      try {
        await this.loginByPassword({
          username: this.devUsername,
          password: this.devPassword,
        });
        await store.dispatch('getUserInfo');
        this.$toast.success('登录成功');
        const redirect = this.$route.query.redirect || '/';
        this.$router.replace(redirect);
      } catch (err) {
        console.error('开发环境登录失败:', err);
        this.$toast.fail(err.errorMsg || err.message || '登录失败，请检查账号和密码');
      } finally {
        this.devLoginLoading = false;
      }
    },
  },
  mounted() {
    this.redirectUri = location.href;
    this.loadSignup();
    
    // 检测PC端微信环境并显示提示
    const ua = navigator.userAgent.toLowerCase();
    const isPCWeChat = (/windows wechat/i.test(ua) || /macwechat/i.test(ua)) && !/mobile|android|iphone|ipad|ipod/i.test(ua);
    
    if (isPCWeChat && !store.getters.openId) {
      this.showPCWeChatTip = true;
      this.$toast({
        message: 'PC端微信需要刷新页面获取授权',
        duration: 3000
      });
    }
  },
  computed: {
    ...mapGetters(['platform', 'verifyTime', 'openId']),
    isDev() {
      return isLocalDevelopment();
    },
    disabledLogin() {
      for (let key in this.form) {
        if (this.form[key] == '') {
          return true;
        }
      }
      return false;
    },
    applyTitle() {
      if (this.applyStatus === 'rejected') return this.canResubmit ? '审核未通过' : '账号已停用';
      return '资料已提交，等待审核';
    },
    signupButtonText() {
      if (!this.signupLoaded) return sessionStorage.getItem('bid_signup_token') ? '重新加载注册资料' : '重新获取微信授权';
      return this.isResubmitting ? '修改资料并重新提交审核' : '确认资料并提交审核';
    },
  },
  watch: {
    verifyTime: {
      immediate: true,
      handler(val) {
        let _this = this;
        if (val && this.timer == null) {
          setTimeout(() => {
            this.timer = setInterval(tempFuc(), 1000);
          }, 10);
        }
        function tempFuc() {
          let currentDateTime = new Date().getTime();
          let dateTimeDiff = val - currentDateTime;
          if (dateTimeDiff > 1000) {
            _this.verifyText = `${parseInt(dateTimeDiff / 1000)}秒后获取`;
            _this.disabledVerify = true;
          } else {
            _this.disabledVerify = false;
            _this.verifyText = '获取验证码';
            // _this.setVerifyDateTime(null);
            _this.closeTimer();
          }
          return tempFuc;
        }
      },
    },
  },
  beforeRouteEnter(to, from, next) {
    showDebugInfo('路由进入开始...');
    showDebugInfo(`当前时间: ${new Date().toLocaleString()}`);
    showDebugInfo(`浏览器信息: ${navigator.userAgent}`);
    showDebugInfo(`当前URL: ${window.location.href}`);
    
    if (isLocalDevelopment()) {
      next();
      return;
    }

    const baseUrl = `${location.protocol}//${location.host}${router.options.base || ''}${to.fullPath}`;
    const redirectParam = from.fullPath ? `redirect=${encodeURIComponent(from.fullPath)}` : '';
    const href = redirectParam ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${redirectParam}` : baseUrl;
    
    let platform = store.getters.platform;
    
    showDebugInfo(`重定向URL: ${href}`);
    showDebugInfo(`平台: ${platform}`);
    
    // 检测PC端微信环境
    const ua = navigator.userAgent.toLowerCase();
    const isPCWeChat = (/windows wechat/i.test(ua) || /macwechat/i.test(ua)) && !/mobile|android|iphone|ipad|ipod/i.test(ua);
    
    if (isPCWeChat) {
      showDebugInfo('检测到PC端微信环境，增强登录处理...');
      // PC端微信可能需要手动刷新获取openId
      if (!store.getters.openId) {
        showDebugInfo('PC端微信未检测到openId，提示用户刷新');
        // 延迟执行，确保页面加载完成
        setTimeout(() => {
          if (window.Toast) {
            window.Toast({
              message: 'PC端微信需要刷新页面获取授权，请刷新重试',
              duration: 4000,
              forbidClick: true
            });
          }
        }, 1000);
      }
    }
    
    if (platform == 'wx') {
      showDebugInfo('检测到微信平台，准备登录...');
      wxLogin(href).then((login) => {
        showDebugInfo(`微信登录结果: ${login}`);
        if (login) {
          showDebugInfo('登录成功，准备跳转...');
          next(to.query.redirect || '/');
        } else {
          showDebugInfo('登录失败，继续...');
          next();
        }
      }).catch((error) => {
        console.error('微信授权失败', error);
        next(vm => vm.$toast.fail('微信授权失败，请点击重新微信授权'));
      });
    } else {
      showDebugInfo('非微信平台，直接继续...');
      next();
    }
  }
};

function isLocalDevelopment() {
  const host = window.location.hostname || '';
  return host === 'localhost' || host === '127.0.0.1' || process.env.NODE_ENV === 'development';
}
</script>
<style scoped lang="scss">
.login-wrap {
  position: relative;
  min-height: 100vh;
  padding-bottom: 64px;
  .header {
    margin: 40px 0;
    font-size: 14px;
  }
  .van-cell-group {
    margin: 16px;
    margin-top: 0;
    border-radius: 6px;
    overflow: hidden;
    .van-cell {
      padding: 18px 16px;
    }
    .verifyCodeBtn {
      padding: 2px 12px;
      color: white;
      border-radius: 4px;
      background-color: #8565bd;
    }
    .phone-tip {
      padding: 8px 16px;
      font-size: 11px;
      color: #ff4757;
      line-height: 1.5;
      font-weight: 400;
      letter-spacing: 0.5px;
      opacity: 0.9;
    }
    .draft-tip {
      margin: 0;
      padding: 0 16px 8px;
      color: #969799;
      font-size: 12px;
      line-height: 1.5;
    }
    .signup-load-error {
      margin: 0;
      padding: 0 16px 12px;
      color: #ee0a24;
      font-size: 12px;
      line-height: 1.5;
    }
    .loginBtn {
      margin: 20px 16px;
      height: 42px;
      width: auto;
      color: white;
      border-radius: 4px;
      background-color: #8565bd;
    }
    
  }
  
  .apply-status {
    padding: 40px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    width: 100%;
    
    .status-icon {
      margin-bottom: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    
    .status-title {
      font-size: 18px;
      font-weight: 600;
      color: #323233;
      margin-bottom: 16px;
      text-align: center;
    }
    
    .status-message {
      font-size: 14px;
      color: #646566;
      line-height: 1.6;
      margin-bottom: 12px;
      padding: 0 10px;
      text-align: center;
      max-width: 280px;
      word-wrap: break-word;
    }

    .reject-reason {
      width: 100%;
      box-sizing: border-box;
      margin: 0 0 16px;
      padding: 12px 16px;
      border: 1px solid #ffccc7;
      border-radius: 8px;
      background: #fff2f0;
      color: #323233;
      text-align: left;

      strong {
        color: #ee0a24;
        font-size: 14px;
      }

      p {
        margin: 6px 0 0;
        color: #646566;
        font-size: 13px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }

    .resubmit-tip {
      margin: 0 16px 12px;
      padding: 12px 14px;
      border: 1px solid #ffccc7;
      border-radius: 8px;
      background: #fff2f0;

      strong { color: #ee0a24; font-size: 14px; }
      p { margin: 6px 0 0; color: #646566; font-size: 13px; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }
    }

    .submitted-summary {
      width: 100%;
      margin: 8px 0 20px;
      overflow: hidden;
      border: 1px solid #ebedf0;
      border-radius: 8px;
      text-align: left;
    }

    .submitted-receipt {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 16px;
      border-top: 1px solid #ebedf0;
      background: #fff;

      strong, span {
        display: block;
      }

      strong {
        margin-bottom: 5px;
        color: #323233;
        font-size: 14px;
      }

      span {
        color: #969799;
        font-size: 12px;
        word-break: break-all;
      }
    }
    
    .status-tip {
      font-size: 12px;
      color: #969799;
      line-height: 1.4;
      text-align: center;
    }
    
    .copy-phone-btn {
      margin-top: 20px;
      padding: 8px 16px;
      background-color: #f7f8fa;
      border: 1px solid #ebedf0;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 14px;
      color: #323233;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:active {
        background-color: #e8e8e8;
        transform: scale(0.98);
      }
      
      .van-icon {
        color: #646566;
      }
    }
  }
  
  // PC端微信提示样式（预留底部安全区）
  .pc-wechat-tip {
    position: fixed;
    bottom: calc(80px + env(safe-area-inset-bottom, 0px));
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 193, 7, 0.95);
    color: #856404;
    padding: 12px 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
    z-index: 1000;
    max-width: 90%;
    
    .van-icon {
      color: #856404;
      flex-shrink: 0;
    }
    
    span {
      flex: 1;
      line-height: 1.4;
    }
    
    .van-button {
      flex-shrink: 0;
      height: 28px;
      padding: 0 12px;
      font-size: 12px;
      background: #856404;
      border-color: #856404;
    }
  }
  
  .footer {
    flex-grow: 1;
    margin-bottom: 32px;
    bottom: 0;
    text-align: center;
    font-size: 12px;
    color: #999999;
  }
}
.version-info {
  position: fixed;
  bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #999;
  text-align: center;
  z-index: 1;
}

/* 开发环境登录区域 */
.dev-login {
  margin: 16px;
  margin-top: 12px;
}
.dev-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  padding: 0 4px;
}
.dev-login .van-cell-group {
  margin: 0 0 12px 0;
}
.dev-tip {
  margin: 0 4px 16px;
  color: #999;
  font-size: 12px;
}
.dev-login-button {
  height: 42px;
  border-radius: 6px;
}
</style>
